/* eslint-disable no-case-declarations */
import { sortBy } from 'lodash';
import { securityTypes } from 'common/constants';
import {
  CT_ONE_DECIMAL_DISPLAY_DECIMALS,
  CT_ONE_DECIMAL_ROW_NUMBERS,
  CURRENT_OWNERSHIP_ROW_NUMBER,
  FIRM_SHARES_ALIAS,
  SHARES_OUTSTANDING_ROW_NUMBER,
} from 'common/constants/cap-table';
import buildRoundedSumExpr from 'pages/CapTable/fund-ownership/utilities/buildRoundedSumExpr';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { CapTableCell, CaptableColumn, CapTableRow } from '../components/captable-table/CaptableTable.props';

const totalParser = ({ cells, columns, rows }: { cells: Record<string, CapTableCell>, columns: CaptableColumn[], rows: CapTableRow[] }) => {
  let totalCells: Record<string, CapTableCell> = {};

  const alphabet = alphabetGenerator([], columns.length);

  // Sort all columns by creation date
  const sortedColumns = sortBy(columns, ['order']);

  const notOptionPoolKeyList = (rowNumber: number) =>
    sortedColumns.reduce((list: string[], column: CaptableColumn, columnIndex: number) => {
      const key = `${alphabet[columnIndex]}${rowNumber}`;

      if (cells[key] && column.security_type !== securityTypes.UNISSUED_OPTIONS) {
        list.push(key);
      }

      return list;
    }, []);
  const notConvertibleNoteKeyList = (rowNumber: number) =>
    sortedColumns.reduce((list: string[], column: CaptableColumn, columnIndex: number) => {
      const key = `${alphabet[columnIndex]}${rowNumber}`;

      if (cells[key] && column.security_type !== securityTypes.CONVERTIBLE_NOTES) {
        list.push(key);
      }

      return list;
    }, []);

  rows.forEach((row, rowIndex) => {
    const rowNumber = rowIndex + 1;
    // Use string concat so a missing legend never becomes NaN via `undefined + rowNumber`.
    const columnLegend = alphabet[0] ?? '';
    const keyActual = `${columnLegend}${rowNumber}`;
    const key = `TOTAL_${columnLegend}${rowNumber}`;

    const expr = () =>
      Object.keys(columns)
        .map((columnIndex: string) => {
          const legend = alphabet[+columnIndex];
          return legend ? `${legend}${rowNumber}` : '';
        })
        .filter(cellKey => cellKey && cells[cellKey]);

    const cell = {
      ...row,
      readOnly: true,
      key: keyActual,
      isTotal: true,
      alias: row.alias,
      defaultTooltip: null,
      expr: rowIndex === 0 ? 'Total' : undefined,
      className: [row.className, 'total-cell'].join(' '),
      linkedCellSymbols: undefined,
    } as CapTableCell;

    if (row.hasTotal) {
      switch (rowNumber) {
        case CURRENT_OWNERSHIP_ROW_NUMBER:
          const notOptionPoolList = notOptionPoolKeyList(CURRENT_OWNERSHIP_ROW_NUMBER);

          if (notOptionPoolList.length) {
            cell.expr = `=sum(${notOptionPoolList})`;
          }
          break;
        case SHARES_OUTSTANDING_ROW_NUMBER:
          const notNoteList = notConvertibleNoteKeyList(SHARES_OUTSTANDING_ROW_NUMBER);

          if (notNoteList.length) {
            cell.expr = buildRoundedSumExpr(notNoteList, CT_ONE_DECIMAL_DISPLAY_DECIMALS);
          }
          break;
        default:
          {
            const keyList = expr();
            if (keyList.length) {
              // Only firm/fund share rows need 1-decimal rounding (ownership is a % and liq pref is currency).
              const isSharesTotalRow =
                row.alias === FIRM_SHARES_ALIAS || row.parent === FIRM_SHARES_ALIAS;
              cell.expr =
                CT_ONE_DECIMAL_ROW_NUMBERS.has(rowNumber) || isSharesTotalRow
                  ? buildRoundedSumExpr(keyList, CT_ONE_DECIMAL_DISPLAY_DECIMALS)
                  : `=sum(${keyList})`;
            }
          }
          break;
      }
    }

    totalCells = {
      ...totalCells,
      [key]: cell,
    };
  });

  return totalCells;
};

export default totalParser;
